/**
 * 「引導註冊的配送員」（courierId）的跨流程攜帶工具。
 *
 * 配送員在 DAPP 幫客戶產序號時，aw-api 產生的 LIFF 註冊連結會附
 * `?invite=<序號>&courierId=<courier_id>`。courier_id 主要存放在後端
 * DynamoDB 的邀請碼記錄，這裡帶的是備援：後端記錄被刷新掉 courier_id 時，
 * 仍能靠 URL 這一份做「哪位配送員帶進這位客戶」歸因。
 *
 * 與 shopInviteCode 同手法：進站時攔 URL 存 sessionStorage，跨 LINE login
 * redirect round-trip 存活，註冊送出（/line/register）時帶 `courier_id`，
 * 用畢即清。後端會驗證該配送員隸屬序號所屬瓦斯行，不符只是忽略、不擋註冊。
 */
const KEY = "referred_courier_id";
/** 客戶掃的是哪一張訂單的 QR（連結上的 `?orderId=`），與 courierId 同進同出。 */
const ORDER_KEY = "referred_order_id";

/** courier_id 形如 ADCOU_12；只留英數 / 底線 / 連字號，上限 64 碼；不合法回空字串。 */
const sanitize = (raw: string | null | undefined): string =>
  (raw ?? "").trim().replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64);

/** 進站時呼叫：若目前 URL 帶 `?courierId=`，存進 sessionStorage。沒有時不動既有值。 */
export const captureReferredCourierFromUrl = (): void => {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const courierId = sanitize(params.get("courierId"));
    if (courierId) sessionStorage.setItem(KEY, courierId);
    const orderId = sanitize(params.get("orderId"));
    if (orderId) sessionStorage.setItem(ORDER_KEY, orderId);
  } catch {
    // 忽略：取不到就當作沒有 courierId / orderId
  }
};

/** 取得已攜帶的訂單 id（客戶掃的那張單）；沒有則 null。 */
export const getReferredOrderId = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const orderId = sanitize(sessionStorage.getItem(ORDER_KEY));
    return orderId || null;
  } catch {
    return null;
  }
};

/** 取得已攜帶的配送員 id；沒有則 null。 */
export const getReferredCourierId = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const courierId = sanitize(sessionStorage.getItem(KEY));
    return courierId || null;
  } catch {
    return null;
  }
};

/** 用掉/離開後清除（courierId 與 orderId 一起），避免殘留讓下一位（同分頁）註冊者被錯誤歸因。 */
export const clearReferredCourierId = (): void => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(KEY);
    sessionStorage.removeItem(ORDER_KEY);
  } catch {
    // 忽略
  }
};
